import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
    CheckCircle,
    AlertTriangle,
    XCircle,
    RefreshCw,
    Clock,
    Loader2,
    Link2,
    ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

export default function ConnectionStatusDashboard() {
    const { api } = useAuth();
    const [connections, setConnections] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [connectionsRes, summaryRes] = await Promise.all([
                api.get('/oauth-settings/connection-status'),
                api.get('/oauth-settings/connection-status/summary')
            ]);
            setConnections(connectionsRes.data);
            setSummary(summaryRes.data);
        } catch (error) {
            console.error('Failed to fetch connection status:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'valid':
                return 'text-green-500';
            case 'expiring_soon':
                return 'text-yellow-500';
            case 'expired':
                return 'text-red-500';
            default:
                return 'text-muted-foreground';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'valid':
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'expiring_soon':
                return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
            case 'expired':
                return <XCircle className="h-4 w-4 text-red-500" />;
            default:
                return <Clock className="h-4 w-4 text-muted-foreground" />;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'valid':
                return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Valid</Badge>;
            case 'expiring_soon':
                return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Expiring Soon</Badge>;
            case 'expired':
                return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Expired</Badge>;
            default:
                return <Badge variant="secondary">Unknown</Badge>;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[300px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            {summary && (
                <div className="grid gap-4 md:grid-cols-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>Total Accounts</CardDescription>
                            <CardTitle className="text-3xl">{summary.total_accounts}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>Connected</CardDescription>
                            <CardTitle className="text-3xl text-green-500">{summary.connected}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>Expiring Soon</CardDescription>
                            <CardTitle className="text-3xl text-yellow-500">{summary.expiring_soon}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>Expired</CardDescription>
                            <CardTitle className="text-3xl text-red-500">{summary.expired}</CardTitle>
                        </CardHeader>
                    </Card>
                </div>
            )}

            {/* Platform Summary */}
            {summary && Object.keys(summary.platforms).length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Platforms Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-3">
                            {Object.entries(summary.platforms).map(([platform, data]) => (
                                <div 
                                    key={platform}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50"
                                >
                                    <span className="text-lg">{data.emoji}</span>
                                    <span className="font-medium">{data.name}</span>
                                    <Badge variant="secondary">{data.count}</Badge>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Connection List */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Connected Accounts</CardTitle>
                        <CardDescription>
                            Monitor the status of your social media connections
                        </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchData}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                </CardHeader>
                <CardContent>
                    {connections.length === 0 ? (
                        <div className="text-center py-8">
                            <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                            <p className="text-muted-foreground">No accounts connected yet</p>
                            <Button asChild className="mt-4">
                                <Link to="/accounts">Connect Accounts</Link>
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {connections.map((connection) => (
                                <div
                                    key={connection.id}
                                    className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                                    data-testid={`connection-${connection.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        {/* Profile Picture */}
                                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                                            {connection.profile_picture ? (
                                                <img
                                                    src={connection.profile_picture}
                                                    alt={connection.account_name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <span className="text-2xl">{connection.emoji}</span>
                                            )}
                                        </div>

                                        {/* Account Info */}
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{connection.account_name}</span>
                                                <span className="text-lg">{connection.emoji}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <span>{connection.platform_name}</span>
                                                {connection.connected_at && (
                                                    <>
                                                        <span>•</span>
                                                        <span>
                                                            Connected {formatDistanceToNow(new Date(connection.connected_at), { addSuffix: true })}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="flex items-center gap-4">
                                        {/* Token Expiry Info */}
                                        <div className="text-right">
                                            {connection.token_status === 'expired' ? (
                                                <p className="text-sm text-red-500 font-medium">Token expired</p>
                                            ) : connection.token_status === 'expiring_soon' ? (
                                                <p className="text-sm text-yellow-500">
                                                    Expires in {connection.days_until_expiry} days
                                                </p>
                                            ) : connection.token_expires_at ? (
                                                <p className="text-sm text-muted-foreground">
                                                    Expires {format(new Date(connection.token_expires_at), 'MMM d, yyyy')}
                                                </p>
                                            ) : (
                                                <p className="text-sm text-muted-foreground">No expiry info</p>
                                            )}
                                        </div>

                                        {/* Status Badge */}
                                        {getStatusBadge(connection.token_status)}

                                        {/* Reconnect Button for expired/expiring */}
                                        {(connection.token_status === 'expired' || connection.token_status === 'expiring_soon') && (
                                            <Button asChild variant="outline" size="sm">
                                                <Link to="/accounts">
                                                    <RefreshCw className="mr-2 h-4 w-4" />
                                                    Reconnect
                                                </Link>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="flex gap-4">
                <Button asChild>
                    <Link to="/accounts">
                        <Link2 className="mr-2 h-4 w-4" />
                        Manage Accounts
                    </Link>
                </Button>
                <Button asChild variant="outline">
                    <Link to="/settings">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        OAuth Settings
                    </Link>
                </Button>
            </div>
        </div>
    );
}
